import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { setAccessToken, setAuthFailureHandler } from '@/api/client';
import { loginWithPassword, logoutOnServer, refreshAccess, type TokenPair } from '@/api/auth';
import { decodeAccess, isExpired, type AccessClaims } from '@/lib/jwt';
import { setStorageUser, storage } from '@/lib/storage';
import {
  fetchUserSettings,
  flushUserSettings,
  putUserSettings,
  useSettingsStore,
} from '@/api/bi/settings';
import { clearLandingRedirectFlag } from '@/lib/landing';

interface AuthState {
  ready: boolean;
  user: AccessClaims | null;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Replace session tokens (e.g. after password change) without re-entering credentials. */
  applyTokenPair: (pair: TokenPair) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ready: false, user: null, isAuthenticated: false });

  const apply = useCallback((access: string | null, refresh: string | null) => {
    setAccessToken(access);
    storage.setRefresh(refresh);
    const claims = access ? decodeAccess(access) : null;
    // Switch the namespaced cache to (or away from) this user *before* we
    // mark auth as ready, so consumers reading prefs on first paint see the
    // right scope.
    setStorageUser(claims?.id ?? null);
    // Clear the per-tab landing-applied flag whenever auth changes (login,
    // refresh, or logout) so the next visit to `/` honours the freshly-loaded
    // user's `landing_path` again.
    if (!claims) clearLandingRedirectFlag();
    setState({ ready: true, user: claims, isAuthenticated: !!claims });
  }, []);

  // Bootstrap: try refresh on app load if a refresh token is present.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const refresh = storage.getRefresh();
      if (!refresh) {
        setState((s) => ({ ...s, ready: true }));
        return;
      }
      try {
        const r = await refreshAccess(refresh);
        if (cancelled) return;
        apply(r.access, r.refresh ?? refresh);
      } catch {
        if (cancelled) return;
        apply(null, null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apply]);

  // Hook the global auth-failure handler so 401s bounce to login.
  useEffect(() => {
    setAuthFailureHandler(() => apply(null, null));
    return () => setAuthFailureHandler(null);
  }, [apply]);

  // Soft-refresh shortly before the access token expires so users don't see flicker mid-action.
  useEffect(() => {
    if (!state.user) return;
    const ms = Math.max(5_000, (state.user.exp - 60) * 1000 - Date.now());
    const t = window.setTimeout(async () => {
      const refresh = storage.getRefresh();
      if (!refresh || isExpired(state.user)) return;
      try {
        const r = await refreshAccess(refresh);
        apply(r.access, r.refresh ?? refresh);
      } catch {
        apply(null, null);
      }
    }, ms);
    return () => window.clearTimeout(t);
  }, [state.user, apply]);

  const login = useCallback<AuthContextValue['login']>(async (username, password) => {
    const pair = await loginWithPassword(username, password);
    apply(pair.access, pair.refresh);
  }, [apply]);

  const applyTokenPair = useCallback<AuthContextValue['applyTokenPair']>((pair) => {
    apply(pair.access, pair.refresh);
  }, [apply]);

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    // Flush any in-flight pref writes so the user doesn't lose the toggle
    // they just changed seconds before clicking "Sign out".
    try {
      await flushUserSettings();
    } catch {
      /* noop — best effort */
    }
    const refresh = storage.getRefresh();
    if (refresh) await logoutOnServer(refresh);
    apply(null, null);
  }, [apply]);

  // Bootstrap per-user settings once auth is ready: pull from the server,
  // and if the server row is empty but the local cache has prior data, push
  // it up once so existing single-device users don't lose their preferences.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchUserSettings();
        if (cancelled) return;
        const isEmpty = !resp.data || Object.keys(resp.data).length === 0;
        if (isEmpty) {
          const localBlob = storage.snapshotForServer();
          if (Object.keys(localBlob).length > 0) {
            try {
              const seeded = await putUserSettings(localBlob, resp.version);
              if (!cancelled) useSettingsStore.getState().setFromServer(seeded);
              return;
            } catch {
              // Fall through and accept the empty server state.
            }
          }
        }
        if (!cancelled) useSettingsStore.getState().setFromServer(resp);
      } catch {
        // Settings are non-critical for first paint; we silently fall back
        // to the local cache. The next page load will retry.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.isAuthenticated]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, applyTokenPair }),
    [state, login, logout, applyTokenPair],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
